import { useEffect, useState } from 'react';
import { useQRCode } from '../useQRCode';
import { useCrypto } from './useCrypto';

export const CRYPTO_DEPOSIT_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  READY: 'ready',
  ERROR: 'error',
} as const;

export type CryptoDepositStatus = (typeof CRYPTO_DEPOSIT_STATUS)[keyof typeof CRYPTO_DEPOSIT_STATUS];

export type UseCryptoDepositReturn = {
  status: CryptoDepositStatus;
  address: string | null;
  qrCodeSrc: string | null;
  isDepositPending: boolean;
  isQrCodeLoading: boolean;
  hasError: boolean;
  copied: boolean;
  triggerDeposit: () => void;
  handleCopyAddress: () => Promise<void>;
};

export const useCryptoDeposit = (paymentProvider: string | null): UseCryptoDepositReturn => {
  const {
    depositMutation: { mutate: requestDeposit, data, isPending: isDepositPending, isError: hasError },
  } = useCrypto();
  const [copied, setCopied] = useState(false);
  const address = isDepositPending ? null : (data?.address ?? null);
  const { src: qrCodeSrc, isLoading: isQrCodeLoading } = useQRCode(address, 140);

  useEffect(() => {
    requestDeposit({});
  }, [paymentProvider, requestDeposit]);

  useEffect(() => {
    if (!copied) {
      return;
    }

    const timeoutId = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timeoutId);
  }, [copied]);

  const triggerDeposit = () => {
    setCopied(false);
    requestDeposit({});
  };

  const handleCopyAddress = async () => {
    if (!address) {
      return;
    }

    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
    } catch {
      /* silently fail */
    }
  };

  const getStatus = (): CryptoDepositStatus => {
    if (hasError) {
      return CRYPTO_DEPOSIT_STATUS.ERROR;
    }

    if ((isDepositPending && !address) || isQrCodeLoading) {
      return CRYPTO_DEPOSIT_STATUS.LOADING;
    }

    if (address && !isQrCodeLoading) {
      return CRYPTO_DEPOSIT_STATUS.READY;
    }

    return CRYPTO_DEPOSIT_STATUS.IDLE;
  };

  const status = getStatus();

  return {
    status,
    address,
    qrCodeSrc,
    isDepositPending,
    isQrCodeLoading,
    hasError,
    copied,
    triggerDeposit,
    handleCopyAddress,
  };
};
